import { Heading } from "@/components/heading/Heading";
import { HeadingSection } from "@/components/heading/HeadingSection";
import { type CoWorkerCalendarOutlook } from "@/features/calendar/types";
import {
  Accordion,
  AccordionItem,
  Button,
  ListView,
  ListViewItem,
  Text,
  Variables,
  checkmarkIcon,
  clockIcon,
  locationPinSmallIcon,
  lockIcon,
  mailIcon,
  personIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ElementsContainer,
  FlexDiv,
  Line,
  LocalizedDateTimeRange,
  ModalSheet,
  RegionContainer,
  SectionContainer,
  translateResponseStatus,
} from "@repo/ui";

interface OutlookEventModalProps {
  isOpen: boolean;
  item: CoWorkerCalendarOutlook;
  closeModal: () => void;
}

export const OutlookEventModal = ({
  isOpen,
  closeModal,
  item,
}: OutlookEventModalProps) => {
  return (
    <ModalSheet
      title={t("label.outlook_event")}
      visible={isOpen}
      onClose={closeModal}
    >
      <HeadingSection value={3}>
        <RegionContainer>
          <SectionContainer>
            <FlexDiv
              justifyContent="space-between"
              alignItems="center"
              gap={`${Variables.space50}`}
            >
              <Heading textStyle="Heading.M">{item.title}</Heading>

              <Button
                size="small"
                text={t("action.open_in_outlook")}
                iconOnly
                ssrIcon={mailIcon}
                onClick={() => {
                  window.open(item.webLink, "_blank");
                }}
              />
            </FlexDiv>

            <ListView
              id={`outlook-event-details-${item.id}`}
              showDivider={false}
            >
              <ListViewItem
                leadingIcon={clockIcon}
                title={t("label.date_and_time")}
                description={
                  item.start ? (
                    <LocalizedDateTimeRange
                      fromDate={item.start}
                      toDate={item.end}
                    />
                  ) : (
                    "-"
                  )
                }
              />
              <ListViewItem
                leadingIcon={locationPinSmallIcon}
                title={t("label.location")}
                description={
                  item.location?.displayName ? item.location.displayName : "-"
                }
              />
              <ListViewItem
                leadingIcon={checkmarkIcon}
                title={t("label.response")}
                description={translateResponseStatus(item.responseStatus)}
              />
              {item.sensitivity === "private" && (
                <ListViewItem
                  leadingIcon={lockIcon}
                  title={t("label.sensitivity")}
                  description={t("label.private")}
                />
              )}
            </ListView>
            {item.onlineMeeting?.joinUrl && (
              <Button
                size="small"
                type="emphasised"
                href={item.onlineMeeting?.joinUrl}
                newWindow={true}
                text={t("action.join_teams_meeting")}
              />
            )}

            {item.description && (
              <>
                <Line />
                <ElementsContainer>
                  <HeadingSection>
                    <Heading textStyle="Heading.XS">
                      <T translationKey="label.description" />
                    </Heading>
                  </HeadingSection>

                  <Text pre overflowHidden>
                    {item.description}
                  </Text>
                </ElementsContainer>
              </>
            )}
            <Accordion>
              {item.attendees && (
                <AccordionItem
                  title={t("label.participants", {
                    count: item.attendees.length,
                  })}
                  id="outlook-event-attendees"
                >
                  <ListView id="attendees">
                    {item.attendees.map((attendee, i) => (
                      <ListViewItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        leadingIcon={personIcon}
                        title={attendee?.emailAddress?.name}
                        description={translateResponseStatus(
                          attendee?.status?.response
                        )}
                      />
                    ))}
                  </ListView>
                </AccordionItem>
              )}
            </Accordion>
          </SectionContainer>
        </RegionContainer>
      </HeadingSection>
    </ModalSheet>
  );
};
